import { gql, useLazyQuery } from "@apollo/client";
import { useEffect, useState, useContext } from "react";
import { convertToCSV } from "@solsys/solsys-common";
import {
  OptionsMenu,
  Button,
  NotificationContext,
  ResponsiveTableView,
} from "@solsys/solsys-reactjs";
import { DateTime } from "luxon";
import { FaPlus, FaCog, FaFileCsv } from "react-icons/fa";
import CraneDropDown from "../../dropdowns/CraneDropDown";
import { withTimestampSortOptions, resetListSort } from "../../listSort";
import useApolloListData from "../../useApolloListData";

const gqlQuery = gql`
  query serviceList($service_list_request: service_list_request) {
    service(service_list_request: $service_list_request) {
      edges {
        id
        created_at
        service_definition {
          id
          name
        }
        crane {
          id
          name
        }
      }
      totalCount
    }
  }
`;

function mapServiceRow(item) {
  return {
    id: item.id,
    name: item.service_definition?.name,
    crane: item.crane?.name || "—",
    created_at: item.created_at,
  };
}

function formatCreatedAt(iso) {
  if (!iso) return "—";
  const dt = DateTime.fromISO(iso, { zone: "utc" });
  return dt.isValid ? dt.toLocal().toFormat("MMM d, yyyy") : "—";
}

export const ListOptions = {
  sortBy: "sortBy",
  sortDirection: "sortDirection",
  name: "name",
  craneId: "crane_id",
};

export default function ServiceList({ id: entityId, action, options = {}, ...props }) {
  const { addNotification } = useContext(NotificationContext);
  const isAdd = action === "add";

  const [requestState, setRequestState] = props.state;
  let { sortBy, sortDirection, name, craneId } = requestState;
  const setListOptions = (state) =>
    setRequestState({ ...requestState, ...state });
  const [optionSortBy, setOptionSortBy] = useState(sortBy);
  const [optionSortDirection, setOptionSortDirection] = useState(sortDirection);
  const [optionName, setOptionName] = useState(name);
  const [optionCraneId, setOptionCraneId] = useState(craneId);
  const applyOptions = () => {
    setListOptions({
      sortBy: optionSortBy,
      sortDirection: optionSortDirection,
      name: optionName,
      craneId: optionCraneId,
    });
  };
  const resetOptions = () => {
    const defaults = resetListSort();
    setOptionSortDirection(defaults.sortDirection);
    setOptionSortBy(defaults.sortBy);
    setOptionName("");
    setOptionCraneId(null);
  };

  const [isFilterMenuOpen, setIsFilterMenuOpen] = useState(false);
  const [limit, setLimit] = useState(100);

  const sortByOptions = withTimestampSortOptions([
    { text: "ID", sortBy: "id" },
    { text: "Name", sortBy: "name" },
  ]);
  const sortDirectionOptions = [
    {
      text: "A to Z",
      sortDirection: "ASC",
    },
    {
      text: "Z to A",
      sortDirection: "DESC",
    },
  ];

  let variables = {
    service_list_request: {
      filters: [],
      pagination: { limit },
      sorting: [],
    },
  };

  if (options.crane_id) {
    variables.service_list_request.filters.push({
      field: "crane_id",
      operator: "=",
      value: String(options.crane_id),
    });
  }
  if (craneId)
    variables.service_list_request.filters.push({
      field: "crane_id",
      operator: "=",
      value: String(craneId),
    });

  if (sortBy && sortDirection) {
    let sortOption = sortByOptions.find((v) => v.sortBy === sortBy);
    variables.service_list_request.sorting.push({
      field: sortBy,
      direction: sortDirection,
      entity: sortOption?.sortEntity,
    });
  }
  if (name)
    variables.service_list_request.filters.push({
      field: "name",
      operator: "like",
      value: `%${name}%`,
    });

  const [csvQuery, { loading: csvLoading }] = useLazyQuery(gqlQuery);
  const [refetch, { loading, data }] = useLazyQuery(gqlQuery, {
    variables,
    pollInterval: 5000,
    onError: (error) => {
      console.error(error);
      addNotification("bad", "Something went wrong");
    },
  });
  const list = useApolloListData(
    data,
    (localData) => localData?.service?.edges?.map(mapServiceRow)
  );
  useEffect(() => {
    if (props.onStateChange) props.onStateChange(requestState);
    refetch({ variables });
  }, [requestState, limit]);
  const count = data?.service?.totalCount ?? 0;
  if (props.isMaster && props.autoSelect && !loading && !isAdd && !entityId) {
    // props.onSelect(0);
    props.onSelect(list.length ? list[0].id : 0);
  }

  return (
    <OptionsMenu isOpen={ isFilterMenuOpen }>
      <OptionsMenu.Menu>
        <div className="options-tools">
          <Button
            onClick={ () => {
              applyOptions();
              setIsFilterMenuOpen(false);
            } }
            loading={ loading }
          >
            Apply
          </Button>
          <button onClick={ resetOptions }>Reset</button>
          <button onClick={ () => setIsFilterMenuOpen(false) }>Close</button>
        </div>

        <div className="options-section">
          <h3>Filters</h3>

          <div className="option">
            <label>Name:</label>
            <input
              value={ optionName }
              onChange={ (e) => setOptionName(e.target.value) }
            />
          </div>

          { !options.crane_id && (
            <div className="option">
              <label>Crane:</label>
              <CraneDropDown
                selected={ optionCraneId }
                onChange={ (selectedCrane) => setOptionCraneId(selectedCrane) }
                searchEnabled={ true }
                multiple={ false }
                disabled={ false }
              />
            </div>
          ) }
        </div>

        <div className="options-section">
          <h3>Sorting</h3>

          <div className="option">
            <label>Sort By:</label>
            <div>
              <select
                value={ optionSortBy }
                onChange={ (event) => setOptionSortBy(event.target.value) }
              >
                { sortByOptions.map((option) => (
                  <option key={ option.text } value={ option.sortBy }>
                    { option.text }
                  </option>
                )) }
              </select>
            </div>
          </div>

          <div className="option">
            <label>Sort Order:</label>
            <div>
              <select
                value={ optionSortDirection }
                onChange={ (event) => setOptionSortDirection(event.target.value) }
              >
                { sortDirectionOptions.map((option) => (
                  <option key={ option.text } value={ option.sortDirection }>
                    { option.text }
                  </option>
                )) }
              </select>
            </div>
          </div>
        </div>

        <div className="options-tools">
          <Button
            onClick={ () => {
              applyOptions();
              setIsFilterMenuOpen(false);
            } }
            loading={ loading }
          >
            Apply
          </Button>
          <button onClick={ resetOptions }>Reset</button>
          <button onClick={ () => setIsFilterMenuOpen(false) }>Close</button>
        </div>
      </OptionsMenu.Menu>
      <OptionsMenu.Layout>
        <div className="page-header">
          <span className="page-header-title">Services</span>
          <div className="page-header-options">
            <Button
              onClick={ () => {
                variables.service_list_request.pagination = { limit: 10000 };
                csvQuery({ variables }).then((response) => {
                  const csvData = convertToCSV(
                    response?.data?.service?.edges.map((entity) => ({
                      ID: entity.id,
                      Name: entity.service_definition?.name || "",
                      Crane: entity.crane?.name || "",
                      Created: formatCreatedAt(entity.created_at),
                    }))
                  );
                  const blob = new Blob([csvData], {
                    type: "text/csv;charset=utf-8;",
                  });
                  const url = URL.createObjectURL(blob);

                  const link = document.createElement("a");
                  link.href = url;
                  link.setAttribute(
                    "download",
                    `services_${DateTime.now()
                      .toLocaleString(DateTime.DATETIME_MED_WITH_SECONDS)
                      .replace(/:/g, "-")
                      .replace(/, /g, "_")}.csv`
                  );
                  document.body.appendChild(link);
                  link.click();
                  document.body.removeChild(link);
                });
              } }
              loading={ csvLoading }
            >
              <FaFileCsv />
            </Button>
            { props.isMaster && (
              <button onClick={ () => props.onSelect(0) }>
                <FaPlus />
              </button>
            ) }
            <button onClick={ () => setIsFilterMenuOpen(true) }>
              <FaCog />
            </button>
          </div>
        </div>

        <ResponsiveTableView
          style={ { height: "100%" } }
          loading={ loading }
          headers={ [
            {
              label: "ID",
            },
            {
              label: "Name",
            },
            {
              label: "Crane",
            },
            {
              label: "Created",
            },
          ] }
          data={ list.map((entity) => ({
            id: entity.id,
            ID: entity.id,
            Name: entity.name,
            Crane: entity.crane,
            Created: formatCreatedAt(entity.created_at),
            selected: String(entity.id) === String(entityId) && !isAdd,
          })) }
          identityColumnKey="ID"
          onRowClick={ (row) => {
            props.onSelect(row.id, true);
          } }
          totalCount={ count }
          addMoreResults={ () => {
            setLimit(limit + 100);
          } }
        />
      </OptionsMenu.Layout>
    </OptionsMenu>
  );
}
